import React, { useRef, useState } from "react";
import { FileDown, FileUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError } from "./api";

export default function AdminImport() {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const chooseFile = async (file) => {
    if (!file) return;
    setResult(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (!data.rows.length) toast.error("No importable rows found in that file.");
    } catch (e) {
      toast.error(apiError(e, "Could not read that file."));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const goodRows = preview ? preview.rows.filter((r) => r.errors.length === 0) : [];

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/import/commit", { rows: goodRows });
      setResult(data);
      setPreview(null);
      toast.success(data.message);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-import">
      <h1 className="font-serif text-3xl text-ivory sm:text-4xl">Import listings from CSV</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ivory/60">
        Moving listings over from the old site? Fill in the template, upload it here, review the rows,
        then import. Everything arrives as <strong className="text-ivory">drafts</strong> — nothing goes
        live until you publish it, and existing properties are never overwritten.
      </p>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="border border-ivory/10 bg-[#242424] p-6">
          <p className="eyebrow text-ivory/50">Step 1</p>
          <p className="mt-2 text-sm text-ivory">Download the template and fill in one row per property.</p>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/import/template`} data-testid="import-template"
            className="mt-4 inline-flex items-center gap-2 border border-ivory/25 px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-ivory/80 hover:bg-white/10">
            <FileDown className="h-4 w-4" /> Download template
          </a>
        </div>
        <div className="border border-ivory/10 bg-[#242424] p-6">
          <p className="eyebrow text-ivory/50">Step 2</p>
          <p className="mt-2 text-sm text-ivory">Upload your completed CSV to review it before anything is saved.</p>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" data-testid="import-file-input" onChange={(e) => chooseFile(e.target.files?.[0])} />
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="import-choose-file"
            className="mt-4 inline-flex items-center gap-2 border border-ivory/25 px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-ivory/80 hover:bg-white/10 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} Choose CSV file
          </button>
        </div>
        <div className="border border-ivory/10 bg-[#242424] p-6">
          <p className="eyebrow text-ivory/50">Step 3</p>
          <p className="mt-2 text-sm text-ivory">Check the preview below, then import. Rows with problems are skipped.</p>
          <button onClick={commit} disabled={busy || goodRows.length === 0} data-testid="import-commit"
            className="mt-4 inline-flex items-center gap-2 bg-brand px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-ivory hover:bg-brand-dark disabled:opacity-40">
            Import {goodRows.length > 0 ? `${goodRows.length} listing${goodRows.length === 1 ? "" : "s"}` : ""} as drafts
          </button>
        </div>
      </div>

      {result && (
        <div className="mt-6 border border-emerald-400/40 bg-emerald-400/10 p-6" data-testid="import-result">
          <p className="text-sm text-ivory">{result.message}</p>
          {result.skipped > 0 && <p className="mt-1 text-xs text-ivory/60">{result.skipped} row(s) skipped — usually because the reference number already exists.</p>}
        </div>
      )}

      {preview && (
        <div className="mt-6 overflow-x-auto border border-ivory/10" data-testid="import-preview">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-ivory/10 bg-[#1f1f1f] text-[0.65rem] uppercase tracking-[0.18em] text-ivory/50">
                <th className="px-4 py-3">Row</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Ref</th>
                <th className="px-4 py-3">District</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Check</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ivory/10">
              {preview.rows.map((r) => (
                <tr key={r.index} className={r.errors.length ? "bg-[#2a2224]" : "bg-[#242424]"}>
                  <td className="px-4 py-3 text-ivory/50">{r.index}</td>
                  <td className="px-4 py-3 text-ivory">{r.row.title || "—"}</td>
                  <td className="px-4 py-3 text-ivory/60">{r.row.ref || "auto"}</td>
                  <td className="px-4 py-3 text-ivory/60">{r.row.district}</td>
                  <td className="px-4 py-3 text-ivory/60">{r.row.price_value ? `$${r.row.price_value.toLocaleString()}` : "—"}</td>
                  <td className="px-4 py-3 text-ivory/60">{r.row.type}</td>
                  <td className="px-4 py-3">
                    {r.errors.length === 0
                      ? <span className="text-xs font-semibold uppercase tracking-[0.15em] text-emerald-300">Ready</span>
                      : <span className="text-xs text-ivory/70">{r.errors.join("; ")}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
